import { Loading } from '@components/atomic/atoms';
import { RejectModal, SuccessModal } from '@components/atomic/organisms';
import { Lens } from '@icons';
import { InvoiceType } from '@lib/api_ts/invoicesApi';
import { assignOwnershipOnSingleInvoice, removeOwnershipOnSingleInvoice } from '@lib/api_ts/ownershipApi';
import { getUsersByProjectId, UserType } from '@lib/api_ts/usersApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Button, FormControl, Overlay, Spinner, Tooltip } from 'react-bootstrap';
// import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

interface OwnershipCellRendererProps {
	data: InvoiceType;
	context: {
		openRowId: number | null;
		setOpenRowId: Dispatch<SetStateAction<number | null>>;
	};
	invalidateQuery?: () => void;
}

const OwnershipCellRenderer: React.FC<OwnershipCellRendererProps> = ({ data, context, invalidateQuery }) => {
	const { projectId } = useParams<{ projectId: string }>();

	const { openRowId, setOpenRowId } = context;
	const { data: users, isPending: isPendingUsers } = useQuery({
		queryKey: ['usersPerProject', projectId],
		queryFn: () => getUsersByProjectId(projectId),
		enabled: !!projectId,
	});

	const queryClient = useQueryClient();

	// const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const [selectedUsers, setSelectedUsers] = useState<UserType[]>(data.owners || []);
	const handleUserSelect = (user: UserType) => {
		setSelectedUsers((prevSelectedUsers) => {
			if (prevSelectedUsers.some((selectedUser) => selectedUser.id === user.id)) {
				return prevSelectedUsers.filter((selectedUser) => selectedUser.id !== user.id);
			} else {
				return [...prevSelectedUsers, user];
			}
		});
	};

	const [isDirty, setIsDirty] = useState(false);

	const targetRef = useRef<HTMLButtonElement>(null);

	const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (targetRef.current) {
			setTargetElement(targetRef.current);
		}
	}, []);

	const hasNoOwners = data.owners.length === 0;
	const isOpen = openRowId === data.id;

	useEffect(() => {
		const scrollContainer = document.querySelector('.ag-body-viewport'); // potrebbe essere diverso nel tuo caso
		const handleScroll = () => {
			if (!targetRef.current) return;

			const wrapperRect = targetRef.current.getBoundingClientRect();
			// const scrollContainer = document.querySelector('.ag-body-viewport');

			if (!scrollContainer) return;

			const containerRect = scrollContainer.getBoundingClientRect();

			const margin = -10;

			const isFullyVisibleWithinContainer =
				wrapperRect.top >= containerRect.top + margin && wrapperRect.bottom <= containerRect.bottom - margin;

			if (!isFullyVisibleWithinContainer && openRowId === data.id) {
				setOpenRowId(null);
				setSelectedUsers([]);
			}
		};

		// Attacca scroll listener alla griglia
		scrollContainer?.addEventListener('scroll', handleScroll);

		return () => {
			scrollContainer?.removeEventListener('scroll', handleScroll);
		};
	}, [openRowId, setOpenRowId, data.id]);

	const assignOwnershipOnSingleInvoiceMutation = useMutation({
		mutationFn: (owners: number[]) => {
			return assignOwnershipOnSingleInvoice(projectId, data.id, owners);
		},
		onSuccess: () => {
			// Invalidate queries or perform any other side effects
			invalidateQuery ? invalidateQuery() : queryClient.invalidateQueries({ queryKey: ['resumeInvoices', projectId, 'externalSupplies'] });
			if (data.sampleId) {
				queryClient.invalidateQueries({ queryKey: ['samplingStatistics', projectId, data.sampleId] });
			}

			queryClient.invalidateQueries({ queryKey: ['projects-stats', projectId] });
			queryClient.refetchQueries({ queryKey: ['projects-stats', projectId] });
			setOpenRowId(null);
			setSelectedUsers([]);
		},
		onError: (error) => {
			console.error('Error assigning ownership:', error);
			// Handle error, show notification, etc.
		},
	});

	const removeOwnershipOnSingleInvoiceMutation = useMutation({
		mutationFn: () => {
			return removeOwnershipOnSingleInvoice(projectId, data.id);
		},
		onSuccess: () => {
			// Invalidate queries or perform any other side effects
			invalidateQuery ? invalidateQuery() : queryClient.invalidateQueries({ queryKey: ['resumeInvoices', projectId, 'externalSupplies'] });
			if (data.sampleId) {
				queryClient.invalidateQueries({ queryKey: ['samplingStatistics', projectId, data.sampleId] });
			}
			queryClient.invalidateQueries({ queryKey: ['projects-stats', projectId] });
			queryClient.refetchQueries({ queryKey: ['projects-stats', projectId] });
			setOpenRowId(null);
			setSelectedUsers([]);
		},
	});

	return (
		<>
			<div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				{/* {
            hasNoOwners ?
                <> */}
				<Button
					ref={targetRef}
					className={`btn ${hasNoOwners ? 'custom-primary-button' : 'custom-ghost-button'}`}
					style={{
						textDecoration: hasNoOwners ? 'none' : 'underline',
						// textAlign: "left",
						// width: hasNoOwners ? "100%" : "auto",
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
					onClick={() => {
						// setIsDropdownOpen((prevIsDropdownOpen) => !prevIsDropdownOpen)
						setOpenRowId(isOpen ? null : data.id);
					}}
				>
					{hasNoOwners
						? 'Assegna'
						: data.owners.map((owner) => (
								<div
									key={owner.id}
									style={{
										whiteSpace: 'nowrap',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
									}}
								>
									{`${owner.firstName} ${owner.lastName}`}
								</div>
							))}
					{/* {hasNoOwners ? "Assegna" : data.owners.map(owner => `${owner.firstName} ${owner.lastName}`).join(', ')} */}
				</Button>

				<Overlay
					target={targetElement}
					show={isOpen}
					placement="bottom"
					popperConfig={{
						modifiers: [
							{
								name: 'flip',
								enabled: true,
								options: {
									fallbackPlacements: ['top'],
									padding: 10,
								},
							},
						],
					}}
				>
					{(props) => (
						<Tooltip
							{...props}
							style={{
								...props.style,
							}}
							className="custom-tooltip"
						>
							<div style={{ position: 'relative' }} className="p-3">
								<FormControl
									type="text"
									placeholder="Cerca una persona..."
									name="search"
									value={searchTerm}
									onChange={(event) => {
										setSearchTerm(event.target.value);
									}}
								/>
								<Lens style={{ position: 'absolute', right: '28px', top: '50%', transform: 'translateY(-50%)' }} />
							</div>
							<ul className="ps-3 pe-3" style={{ listStyle: 'none', margin: 0, overflowY: 'auto', height: '320px' }}>
								{!users && isPendingUsers ? (
									<Loading />
								) : (
									users
										.filter((user) => `${user.firstName} ${user.lastName} ${user.id}`.toLowerCase().includes(searchTerm.toLowerCase()))
										.map((user) => (
											<li key={user.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
												<input
													id={`${user.id}-ownership-checkbox`}
													type="checkbox"
													style={{ borderColor: '#B3B3B3' }}
													checked={selectedUsers.some((selectedUser) => selectedUser.id === user.id)}
													onChange={() => {
														if (!isDirty) setIsDirty(true);
														handleUserSelect(user);
													}}
													disabled={selectedUsers.length >= 2 && !selectedUsers.some((selectedUser) => selectedUser.id === user.id)}
												/>
												<label
													htmlFor={`${user.id}-ownership-checkbox`}
													style={{
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{user.firstName} {user.lastName} - ID: {user.id}
												</label>
											</li>
										))
								)}
							</ul>
							<div
								className="px-3 py-4 d-grid gap-4"
								style={{
									backgroundColor: '#DEE4F1',
								}}
							>
								<div className="d-flex justify-content-between align-items-center fw-bold">
									<span>Ownership assegnate</span>
									<span>{data.owners.length}/2</span>
								</div>
								<div className="d-flex justify-content-center gap-2">
									<Button
										onClick={() => {
											// setIsDropdownOpen(false)
											setOpenRowId(null);
											setSelectedUsers(data.owners || []);
										}}
										className="btn custom-outline-button"
										style={{
											width: '100%',
										}}
									>
										Annulla
									</Button>

									<Button
										disabled={
											!isDirty ||
											// selectedUsers.length === 0 ||
											assignOwnershipOnSingleInvoiceMutation.isPending ||
											removeOwnershipOnSingleInvoiceMutation.isPending
										}
										onClick={() => {
											const owners = selectedUsers.map((user) => user.id);
											console.log('owners', owners);
											console.log('owners', owners.length);
											if (owners.length === 0) {
												removeOwnershipOnSingleInvoiceMutation.mutate();
												return;
											}
											assignOwnershipOnSingleInvoiceMutation.mutate(owners);
										}}
										className="btn custom-primary-button"
										style={{
											width: '100%',
										}}
									>
										{assignOwnershipOnSingleInvoiceMutation.isPending || removeOwnershipOnSingleInvoiceMutation.isPending ? (
											<Spinner size="sm" animation="border" />
										) : (
											'Conferma'
										)}
									</Button>

									{/* <Button onClick={() => console.log('')} disabled={!isActiveSampling} className="btn new custom-primary-button ms-3">
                                                                Gestisci ownership
                                                                <IcoUserCircle className={`ms-2 ${isActiveSampling ? 'ico-path-fill-white' : ''}`} />
                                                            </Button> */}
									{/* <Button
                                    disabled={!isDirty || selectedUsers.length === 0}
                                    onClick={() => {
                                        const owners = selectedUsers.map(user => user.id);
                                        assignOwnershipOnSingleInvoiceMutation.mutate(owners);

                                    }}
                                    className='btn custom-primary-button' style={{
                                        width: "100%"

                                    }}>
                                    Conferma
                                </Button> */}
								</div>
							</div>
						</Tooltip>
					)}
				</Overlay>
				{/* </>
                : <>
                    {data.owners.map(owner => `${owner.firstName} ${owner.lastName}`).join(', ')}
                </>
        } */}
			</div>
			<SuccessModal
				message="Ownership modificata con successo!"
				show={assignOwnershipOnSingleInvoiceMutation.isSuccess}
				onHide={assignOwnershipOnSingleInvoiceMutation.reset}
			/>
			<RejectModal
				message="Si è verificato un errore durante la modifica dell'ownership."
				show={assignOwnershipOnSingleInvoiceMutation.isError}
				onHide={assignOwnershipOnSingleInvoiceMutation.reset}
				tryAgain={() => {
					const owners = selectedUsers.map((user) => user.id);
					assignOwnershipOnSingleInvoiceMutation.mutate(owners);
				}}
			/>
			<SuccessModal
				message="Ownership modificata con successo!"
				show={removeOwnershipOnSingleInvoiceMutation.isSuccess}
				onHide={removeOwnershipOnSingleInvoiceMutation.reset}
			/>
			<RejectModal
				message="Si è verificato un errore durante la modifica dell'ownership."
				show={removeOwnershipOnSingleInvoiceMutation.isError}
				onHide={removeOwnershipOnSingleInvoiceMutation.reset}
				tryAgain={() => {
					removeOwnershipOnSingleInvoiceMutation.mutate();
				}}
			/>
		</>
	);
};

export default OwnershipCellRenderer;
